import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Settings as GearIcon, Wrench as ToolIcon, Server, Check, ChevronDown, Star, Eye, EyeOff, Info, Bot, ArrowRight, PlusCircle, Trash2, Brain } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { motion, AnimatePresence } from 'framer-motion';
import { botManager } from '@/managers/BotManager';
import { authManager, TIERS } from '@/managers/AuthManager';
import { useConversation } from '@/managers/ConvoManager/ConvoManager';
import { workspaceManager } from '@/managers/workspaceManager';
import CustomBotModal from '@/components/layout/CustomBotModal';
import McpServerAddEditOverlay from './McpServerAddEditOverlay';
import McpServerManageOverlay from './McpServerManageOverlay';
import { useTheme } from '@/managers/ThemeProvider';
import { applyToolPresentationOverrides, isMaintenanceTool, shouldHideToolFromOptions } from '@/utils/toolPresentation';
import {
    DEFAULT_TOOL_ITERATION_LIMIT,
    MAX_TOOL_ITERATION_LIMIT,
    MIN_TOOL_ITERATION_LIMIT,
    getEffectiveActiveServerIds,
    getEffectiveActiveToolIds,
    getEffectiveToolIterationLimit,
    getWorkspaceForConversation,
    resolveWorkspaceAccessScopeId,
} from '@/utils/workspaceAccessSettings';

/* ========================
 * useUnifiedSettings - Hook for managing unified settings state including auth, MCP servers, and tools
 * Provides reactive state updates and management functions
 * ========================*/
const useUnifiedSettings = () => {
    const [authState, setAuthState] = useState(() => authManager.getState());

    useEffect(() => {
        const unsubscribeAuth = authManager.subscribe(setAuthState);
        return () => unsubscribeAuth();
    }, []);

    const allUserMcpServers = useMemo(() => {
        return (authState.mcpServers || []).map(s => ({
            ...s,
            enabled: s.enabled === undefined ? true : Boolean(s.enabled)
        }));
    }, [authState.mcpServers]);

    return {
        mcpServers: allUserMcpServers,
        globalTools: authState.globalTools || [],
        isLoading: !authState.isInitialized || !authState.settingsLoaded || !authState.mcpServersLoaded || !authState.globalToolsLoaded,
        getModelPreferencesForBot: (botId) => authManager.getModelPreferencesForBot(botId),
        starModel: (botId, modelId, starred) => authManager.starModel(botId, modelId, starred),
        hideModel: (botId, modelId, hidden) => authManager.hideModel(botId, modelId, hidden),
        setThinkingPreference: (botId, modelId, enabled, budget) => authManager.setThinkingPreference(botId, modelId, enabled, budget),
        getThinkingPreferences: (botId, modelId) => authManager.getThinkingPreferences(botId, modelId),
        addMcpServer: (serverData) => authManager.addMcpServer(serverData),
        deleteMcpServer: (serverId) => authManager.deleteMcpServer(serverId),
        updateMcpServer: (serverId, updates) => authManager.updateMcpServer(serverId, updates),
        authState: authState,
        hasTierAccess: (requiredTier) => authManager.hasTierAccess(requiredTier),
    };
};

/* ========================
 * PlatformTag - Platform tag component for displaying model platform with visual styling
 * Shows platform name with appropriate colors and opacity based on API key availability
 * ========================*/
const PlatformTag = ({ platform, hasKey = false, size = "md", isDarkTheme }) => {
    const getPlatformName = (p) => {
        const normalized = String(p).toUpperCase();
        if (normalized.startsWith('CUSTOM_')) return 'CUSTOM';

        switch (normalized) {
            case 'LUCENA': return 'LucenaOne';
            case 'OPENAI': return 'OpenAI';
            case 'CLAUDE': return 'Claude';
            case 'GEMINI': return 'Gemini';
            case 'MISTRAL': return 'Mistral';
            default: return p;
        }
    };
    const getTagStyles = (p) => {
        const base = `inline-flex items-center px-1.5 py-0.5 rounded-sm text-[${size === 'sm' ? '9px' : '10px'}] transition-colors duration-200`;
        const keyStateStyle = hasKey ? '' : 'opacity-40';
        const normalized = String(p).toUpperCase();

        let bgColor = '';
        let textColor = '';

        switch (normalized) {
            case 'LUCENA':
                bgColor = isDarkTheme ? 'bg-rose-500/10' : 'bg-rose-500/20';
                textColor = isDarkTheme ? 'text-rose-300' : 'text-rose-800';
                break;
            case 'OPENAI':
                bgColor = isDarkTheme ? 'bg-emerald-500/10' : 'bg-emerald-500/20'; 
                textColor = isDarkTheme ? 'text-emerald-400' : 'text-emerald-800'; 
                break;
            case 'CLAUDE':
                bgColor = isDarkTheme ? 'bg-[#F6A192]/10' : 'bg-[#F6A192]/20';
                textColor = isDarkTheme ? 'text-[#F6A192]' : 'text-[#8B5C52]'; 
                break;
            case 'GEMINI':
                bgColor = isDarkTheme ? 'bg-violet-400/10' : 'bg-violet-400/20';
                textColor = isDarkTheme ? 'text-violet-400' : 'text-violet-800'; 
                break;
            case 'MISTRAL':
                bgColor = isDarkTheme ? 'bg-blue-400/10' : 'bg-blue-400/20';
                textColor = isDarkTheme ? 'text-blue-400' : 'text-blue-800'; 
                break;
            default:
                if (normalized.startsWith('CUSTOM_')) {
                    bgColor = isDarkTheme ? 'bg-amber-500/15' : 'bg-amber-500/20';
                    textColor = isDarkTheme ? 'text-amber-300' : 'text-amber-800';
                } else {
                    bgColor = 'bg-surface-dark';
                    textColor = 'text-on-surface-dim';
                }
                break;
        }
        return `${base} ${bgColor} ${textColor} ${keyStateStyle}`;
    };
    return (<span className={getTagStyles(platform)}>{getPlatformName(platform)}</span>);
};

/* ========================
 * ThinkingControls - Thinking controls component with brain icon toggle and hover popover
 * Displays for thinking-capable models with token budget slider
 * ========================*/
const ThinkingControls = ({ thinkingBudget, onBudgetChange, modelId }) => {
    const [showBudgetPopover, setShowBudgetPopover] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
    const isEffectivelyEnabled = thinkingBudget > 0;

    const handleBrainHover = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPopoverPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
        setShowBudgetPopover(true);
    };

    const handleBudgetSliderChange = (e) => {
        let value = parseInt(e.target.value, 10);
        if (value > 0 && value < 1024) {
            value = 1024;
        }
        onBudgetChange(value);
    };

    const formatBudget = (budget) => {
        if (budget === 0) return 'Off';
        return `${budget.toLocaleString()} t`;
    };
    
    return (
        <>
            <button
                onMouseEnter={handleBrainHover}
                onMouseLeave={() => setShowBudgetPopover(false)}
                onClick={(e) => e.stopPropagation()}
                className="p-0.5 rounded transition-colors hover:bg-surface-dark"
                title={isEffectivelyEnabled ? `Thinking enabled (${thinkingBudget.toLocaleString()} tokens)` : "Thinking disabled"}
            >
                <Brain 
                    size={10} 
                    className={`transition-colors duration-200 ${
                        isEffectivelyEnabled 
                            ? 'text-primary' 
                            : 'text-on-surface-dim hover:text-primary'
                    }`} 
                />
            </button>
            
            <AnimatePresence>
                {showBudgetPopover && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.15 }}
                        className="fixed z-[6000] bg-surface-dark border border-[var(--border-color)] rounded-md p-2 shadow-lg"
                        style={{
                            left: popoverPosition.x,
                            top: popoverPosition.y,
                            transform: 'translateX(-50%) translateY(-100%)'
                        }}
                        onMouseEnter={() => setShowBudgetPopover(true)}
                        onMouseLeave={() => setShowBudgetPopover(false)}
                    >
                        <div className="text-xs text-on-surface-dim mb-1">Thinking Budget</div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min="0"
                                max="32768"
                                step="256"
                                value={thinkingBudget}
                                onChange={handleBudgetSliderChange}
                                onClick={(e) => e.stopPropagation()}
                                className="w-28 h-1.5 rounded-lg appearance-none cursor-pointer slider"
                                style={getRangeFillStyle(thinkingBudget, 0, 32768)}
                            />
                            <span className="text-xs text-on-surface font-medium min-w-[4rem] text-right">{formatBudget(thinkingBudget)}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

const platformOrder = ['OPENAI', 'CLAUDE', 'GEMINI', 'MISTRAL', 'LUCENA'];

const getRangeFillStyle = (value, min, max) => {
    const numericValue = Number(value);
    const numericMin = Number(min);
    const numericMax = Number(max);
    const safeRange = Math.max(1, numericMax - numericMin);
    const clampedValue = Math.min(numericMax, Math.max(numericMin, Number.isFinite(numericValue) ? numericValue : numericMin));
    const percentage = ((clampedValue - numericMin) / safeRange) * 100;

    return {
        background: `linear-gradient(90deg, var(--primary) 0%, var(--primary) ${percentage}%, rgba(var(--primary-rgb), 0.14) ${percentage}%, rgba(var(--primary-rgb), 0.14) 100%)`,
    };
};

/* ========================
 * sortModelsWithPlatformPriority - Sort models by starred status first, then platform priority, then order/name
 * Maintains consistent ordering across the application
 * ========================*/
const sortModelsWithPlatformPriority = (models = [], starredModelIds = []) => {
    return [...models].sort((a, b) => {
        const aIsStarred = starredModelIds.includes(a.id);
        const bIsStarred = starredModelIds.includes(b.id);
        if (aIsStarred && !bIsStarred) return -1;
        if (!aIsStarred && bIsStarred) return 1;
        const platformAIndex = platformOrder.indexOf(a.platform?.toUpperCase());
        const platformBIndex = platformOrder.indexOf(b.platform?.toUpperCase());
        const effectiveAIndex = platformAIndex === -1 ? platformOrder.length : platformAIndex;
        const effectiveBIndex = platformBIndex === -1 ? platformOrder.length : platformBIndex;
        if (effectiveAIndex !== effectiveBIndex) return effectiveAIndex - effectiveBIndex;
        if (platformAIndex === -1 && platformBIndex === -1) { 
            if (a.platform && b.platform) { 
                 if (a.platform.toUpperCase() < b.platform.toUpperCase()) return -1;
                 if (a.platform.toUpperCase() > b.platform.toUpperCase()) return 1;
            } else if (a.platform) { 
                return -1;
            } else if (b.platform) { 
                return 1;
            }
        }
        const orderA = a.order !== undefined ? a.order : Infinity;
        const orderB = b.order !== undefined ? b.order : Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || a.model || '').localeCompare(b.name || b.model || '');
    });
};

/* ========================
 * sortToolsByOrder - Sort tools by disabled status first, then order, then name
 * Keeps disabled tools at the bottom for better UX
 * ========================*/
const sortToolsByOrder = (tools = []) => {
    return [...tools].sort((a, b) => {
        if (a.isDisabled !== b.isDisabled) {
            return a.isDisabled - b.isDisabled;
        }
        const orderA = a.order !== undefined ? a.order : Infinity;
        const orderB = b.order !== undefined ? b.order : Infinity;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
    });
};

/* ========================
 * PanelToggleSwitch - Reusable toggle switch component for panel settings
 * Provides consistent styling and behavior across different toggles
 * ========================*/
const PanelToggleSwitch = ({ enabled, onChange, label, subLabel, disabled = false, id, 'data-tooltip-id': dataTooltipId, 'data-tooltip-content': dataTooltipContent, 'data-tooltip-place': dataTooltipPlace = "left" }) => {
    const handleClick = (e) => { e.stopPropagation(); if (!disabled && onChange) { onChange(); } };
        const railStyle = enabled
                ? { backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }
                : { backgroundColor: 'var(--surface-dark)', borderColor: 'var(--border-subtle)' };

    return (
        <div className="flex items-center justify-between py-1 px-1.5 hover:bg-white/5 rounded">
          <div className="flex-grow mr-2">
            <div className={`text-xs ${disabled ? 'text-on-surface-dim-alpha-70' : 'text-on-surface'} ${!disabled ? 'cursor-pointer' : ''}`} data-tooltip-id={dataTooltipId} data-tooltip-content={dataTooltipContent} onClick={handleClick}>
              {label}
            </div>
            {subLabel && <p className={`text-[10px] -mt-0.5 ${disabled ? 'text-on-surface-dim-alpha-50' : 'text-on-surface-dim'}`}>{subLabel}</p>}
          </div>
                    <button id={id} type="button" onClick={handleClick} disabled={disabled} style={railStyle} className={`relative inline-flex h-4 w-7 items-center rounded-full border transition-colors flex-shrink-0 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`} aria-checked={enabled} role="switch">
                        <span className={`inline-block h-3 w-3 transform rounded-full ${enabled ? 'bg-white' : 'bg-on-surface-dim/90'} shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
          {dataTooltipId && dataTooltipContent && (<Tooltip id={dataTooltipId} place={dataTooltipPlace} style={modalTooltipStyles} border={modalTooltipBorder} noArrow float />)}
        </div>
    );
};

const modalTooltipStyles = { backgroundColor: 'var(--surface-dark)', color: 'var(--on-surface)', fontSize: '0.75rem', padding: '0.3rem 0.6rem', borderRadius: '0.25rem', zIndex: 6000, boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'};
const modalTooltipBorder = '1px solid var(--border-color)';

/* ========================
 * ModelVariationItem - Individual model variation item with selection, starring, hiding, and thinking controls
 * Displays model info with platform tag and interactive controls
 * ========================*/
const ModelVariationItem = ({ 
    modelInfo, 
    isSelected, 
    onSelect, 
    hasApiKey, 
    isStarred, 
    isHidden, 
    onToggleStar, 
    onToggleHide, 
    showControls = true,
    thinkingPrefs,
    onThinkingBudgetChange,
    isDarkTheme
}) => {
    const normalizedPlatform = String(modelInfo.platform || '').toUpperCase();
    const hasThinkingCapability = modelInfo.hasThinking || normalizedPlatform === 'CLAUDE' || normalizedPlatform.startsWith('CUSTOM_');
    const isThinkingEnabled = thinkingPrefs?.enabled ?? (hasThinkingCapability ? true : false);
    
    // Use budget as the source of truth for "enabled" state. 0 is off.
    // Default budget is 1800 if enabled, 0 if disabled.
    const thinkingBudget = thinkingPrefs?.budget ?? (isThinkingEnabled ? 1800 : 0);

    return (
        <div onClick={hasApiKey && !isHidden ? () => onSelect(modelInfo) : undefined}
            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md transition-colors duration-150 group
                ${isSelected && !isHidden ? 'bg-primary/10 ring-1 ring-inset ring-primary/40' : 'hover:bg-surface-dark/40'}
                ${!hasApiKey || isHidden ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                ${isHidden ? 'ml-1 border-l-2 border-transparent hover:border-surface-dark' : ''}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <PlatformTag platform={modelInfo.platform} hasKey={hasApiKey} size="sm" isDarkTheme={isDarkTheme} />
                <div className="flex items-center gap-1 flex-1 min-w-0">
                    {isStarred && !isHidden && (<Star size={8} className="fill-yellow-400 text-yellow-400 flex-shrink-0" />)}
                    <span className={`text-xs truncate ${isSelected && !isHidden ? (isDarkTheme ? 'text-primary font-medium' : 'text-on-surface font-medium') : 'text-on-surface-dim'}`}>
                        {modelInfo.name || modelInfo.model}
                    </span>
                    {hasThinkingCapability && hasApiKey && !isHidden && (
                        <ThinkingControls
                            thinkingBudget={thinkingBudget}
                            onBudgetChange={(budget) => onThinkingBudgetChange(modelInfo.id, budget)}
                            modelId={modelInfo.id}
                        />
                    )}
                </div>
            </div>
            <div className="flex items-center gap-0.5">
                {showControls && hasApiKey && (
                    <>
                        <button onClick={(e) => { e.stopPropagation(); onToggleStar(modelInfo.id, !isStarred); }} className="p-0.5 rounded invisible group-hover:visible focus:visible hover:bg-surface-dark transition-opacity" title={isStarred ? "Unstar model" : "Star model"}>
                            <Star size={10} className={`transition-colors duration-200 ${isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-on-surface-dim hover:text-yellow-400'}`} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onToggleHide(modelInfo.id, !isHidden); }} className="p-0.5 rounded invisible group-hover:visible focus:visible hover:bg-surface-dark transition-opacity" title={isHidden ? "Show model" : "Hide model"}>
                            {isHidden ? <Eye size={10} className="text-on-surface-dim hover:text-primary" /> : <EyeOff size={10} className="text-on-surface-dim hover:text-error" />}
                        </button>
                    </>
                )}
                {isSelected && !isHidden && <Check size={14} className="text-primary ml-1" />}
            </div>
            {!hasApiKey && (<span className="text-[10px] text-amber-500/80 ml-auto mr-1" data-tooltip-id={`apikey-missing-${modelInfo.id}`} data-tooltip-content="API Key Missing!">⚠️</span>)}
            <Tooltip id={`apikey-missing-${modelInfo.id}`} place="top" style={modalTooltipStyles} border={modalTooltipBorder} noArrow float />
        </div>
    );
};

/* ========================
 * BotSwitcherItem - Bot switcher item component for individual bot selection
 * Shows bot info with selection state and click handling
 * ========================*/
const BotSwitcherItem = ({ bot, onSelect, isActive }) => {
    return (
        <div onClick={() => onSelect(bot)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors duration-150
              ${isActive ? 'bg-primary/10 ring-1 ring-inset ring-primary/40' : 'hover:bg-surface-dark/40'} cursor-pointer`}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Bot size={16} className={`${isActive ? 'text-primary' : 'text-on-surface-dim'}`} />
            <span className={`text-sm truncate ${isActive ? 'text-primary font-medium' : 'text-on-surface'}`}>{bot.name}</span>
          </div>
          {isActive && (<Check size={14} className="text-primary ml-1" />)}
        </div>
    );
};

/* ========================
 * BotSwitcherOverlay - Overlay component for bot switching interface
 * Provides scrollable list of available bots with selection handling
 * ========================*/
const BotSwitcherOverlay = ({ isOpen, onClose, onSelectBot, currentBotId, availableBots }) => {
    if (!isOpen) return null;
    return (
        <motion.div className="absolute inset-0 z-10 bg-surface-dark backdrop-blur-sm rounded-lg flex flex-col p-3 shadow-glass border border-[var(--border-color)]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-on-surface">Switch Custom Profile</h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-surface-dark/50 text-on-surface-dim"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1">
            {(availableBots || []).map(bot => (<BotSwitcherItem key={bot.id} bot={bot} isActive={currentBotId === bot.id} onSelect={onSelectBot} />))}
          </div>
        </motion.div>
    );
};

/* ========================
 * NextMessageOptionsModal - Main modal component for next message options
 * Handles bot/model selection, tool toggles, and MCP server management
 * ========================*/
const NextMessageOptionsModal = ({ isOpen, onClose, openMcpManagerOnOpen = false }) => {
    const [portalContainer, setPortalContainer] = useState(null);
    const [showHiddenModelsUIToggle, setShowHiddenModelsUIToggle] = useState(false);
    const [optimisticToolStates, setOptimisticToolStates] = useState({});
    const [optimisticMcpStates, setOptimisticMcpStates] = useState({});
    const [isBotSwitcherOpen, setIsBotSwitcherOpen] = useState(false);
    const [showCustomBotModal, setShowCustomBotModal] = useState(false);
    const [showAllTools, setShowAllTools] = useState(false);
    const [toolIterationLimit, setToolIterationLimit] = useState(DEFAULT_TOOL_ITERATION_LIMIT);
    
    const [showMcpAddEditOverlay, setShowMcpAddEditOverlay] = useState(false);
    const [showMcpManageOverlay, setShowMcpManageOverlay] = useState(false);
    const [serverToEdit, setServerToEdit] = useState(null);
    const [isProcessingMcpServer, setIsProcessingMcpServer] = useState(false);
    const [workspaceState, setWorkspaceState] = useState(() => workspaceManager.getState());

    const { currentConversation, updateConversation, updateConversationBotAndModel, currentConversationId } = useConversation();
    const {
        mcpServers: allUserMcpServers,
        globalTools,
        isLoading: areSettingsLoading,
        getModelPreferencesForBot,
        starModel,
        hideModel,
        setThinkingPreference,
        getThinkingPreferences,
        addMcpServer,
        deleteMcpServer,
        updateMcpServer,
        authState,
        hasTierAccess
    } = useUnifiedSettings();

    const { isDarkTheme } = useTheme();
    const hiddenToolIds = useMemo(() => new Set(['Detect_And_Label']), []);

    useEffect(() => {
        if (isOpen) {
            setOptimisticToolStates({});
            setOptimisticMcpStates({});
            setShowMcpAddEditOverlay(false);
            setShowMcpManageOverlay(openMcpManagerOnOpen);
            setServerToEdit(null);
            setShowAllTools(false);
        }
    }, [isOpen, openMcpManagerOnOpen]);

    const [botManagerState, setBotManagerState] = useState(() => botManager.getState());
    useEffect(() => {
        const unsubscribe = botManager.subscribe(setBotManagerState);
        return () => unsubscribe();
    }, []);
    useEffect(() => workspaceManager.subscribe(setWorkspaceState), []);
    const { activeBotConfig, platformModelsWithKeys, allPlatformModels } = botManagerState;

    const availablePlatforms = useMemo(() => {
        if (!platformModelsWithKeys) return {};
        const platforms = {};
        platformModelsWithKeys.forEach(model => {
            if (model.platform) platforms[model.platform.toUpperCase()] = true;
        });
        return platforms;
    }, [platformModelsWithKeys]);

    const usableModelIds = useMemo(() => {
        return new Set((platformModelsWithKeys || []).map(model => model.id).filter(Boolean));
    }, [platformModelsWithKeys]);
    
    // MODIFIED: This entire `useMemo` block is corrected to use the universal `hasTierAccess` method.
    const availableTools = useMemo(() => {
        const platformsWithKeys = new Set((platformModelsWithKeys || []).map(m => m.platform?.toUpperCase()).filter(Boolean));
        const hasAnyKey = platformsWithKeys.size > 0;
        const userIsBetaMember = authState.settings?.beta === true;

        const processed = globalTools.map(rawTool => {
            const tool = rawTool;
            const presentationTool = applyToolPresentationOverrides(tool);
            const tiers = presentationTool.tiers || [];
            let requiredTierTag = null;
            
            // CORRECTED: Use the single source of truth for tier checking.
            const hasAccess = tiers.length === 0 || tiers.some(t => hasTierAccess(t));

            // Determine the lowest tier tag to show if access is denied.
            if (!hasAccess && tiers.length > 0 && !tiers.includes('Free')) {
                 const tierRank = { 'Free': 0, 'Plus': 1, 'Pro': 2, 'Beta': 2, 'Evok': 3, 'EvokUltra': 4 };
                 const sortedTiers = [...tiers].sort((a, b) => (tierRank[a] ?? 99) - (tierRank[b] ?? 99));
                 const rawTag = sortedTiers[0].toUpperCase();
                 requiredTierTag = rawTag === 'PLUS' ? 'PRO' : rawTag;
            }
    
            let isToolDisabledByKey = false;
            let keyDisabledReason = null;

            if (tool.platform) {
                const requiredPlatform = tool.platform.toUpperCase();
                if (!platformsWithKeys.has(requiredPlatform)) {
                    isToolDisabledByKey = true;
                    keyDisabledReason = `${tool.platform} Key Required`;
                }
            } else if (!hasAnyKey) {
                isToolDisabledByKey = true;
                keyDisabledReason = "API Key Required";
            }

            const isBetaRestricted = tool.beta && !userIsBetaMember;

            const isDisabled = !hasAccess || isToolDisabledByKey || isBetaRestricted;
            
            let finalDisabledReason = keyDisabledReason;
            if (isBetaRestricted) {
                finalDisabledReason = 'Closed Beta';
            }

            return {
                id: presentationTool.nameToPass,
                name: presentationTool.name,
                description: presentationTool.description,
                activeByDefault: presentationTool.activeByDefault === undefined ? true : Boolean(presentationTool.activeByDefault),
                isMaintenance: Boolean(presentationTool.maintenanceMode) || isMaintenanceTool(presentationTool),
                maintenanceLabel: presentationTool.maintenanceLabel || 'MAINT.',
                isDisabled: Boolean(presentationTool.maintenanceMode) || isMaintenanceTool(presentationTool) || isDisabled,
                disabledReason: Boolean(presentationTool.maintenanceMode) || isMaintenanceTool(presentationTool)
                    ? 'Temporarily unavailable'
                    : finalDisabledReason,
                requiredTierTag: requiredTierTag,
                order: presentationTool.order
            };
        });
        return sortToolsByOrder(processed.filter(tool => !shouldHideToolFromOptions(tool) && !hiddenToolIds.has(tool.id)));
    }, [globalTools, platformModelsWithKeys, authState.settings, hasTierAccess, hiddenToolIds]); // Added hasTierAccess to dependency array

    const scopedWorkspace = useMemo(() => getWorkspaceForConversation(workspaceState, currentConversation), [workspaceState, currentConversation]);
    const scopedWorkspaceId = useMemo(() => resolveWorkspaceAccessScopeId(currentConversation), [currentConversation]);
    const defaultActiveToolIds = useMemo(() => availableTools.filter(tool => tool.activeByDefault && !tool.isDisabled).map(tool => tool.id), [availableTools]);
    const defaultActiveServerIds = useMemo(() => allUserMcpServers.filter(server => server.enabled).map(server => server.id), [allUserMcpServers]);
    const effectiveActiveToolIds = useMemo(() => getEffectiveActiveToolIds({
        conversation: currentConversation,
        workspace: scopedWorkspace,
        fallbackActiveToolIds: defaultActiveToolIds,
    }), [currentConversation, scopedWorkspace, defaultActiveToolIds]);
    const effectiveActiveServerIds = useMemo(() => getEffectiveActiveServerIds({
        conversation: currentConversation,
        workspace: scopedWorkspace,
        fallbackActiveServerIds: defaultActiveServerIds,
    }), [currentConversation, scopedWorkspace, defaultActiveServerIds]);
    const effectiveToolIterationLimit = useMemo(() => getEffectiveToolIterationLimit({
        conversation: currentConversation,
        workspace: scopedWorkspace,
        fallbackToolIterationLimit: DEFAULT_TOOL_ITERATION_LIMIT,
    }), [currentConversation, scopedWorkspace]);

    useEffect(() => {
        if (isOpen) {
            setToolIterationLimit(effectiveToolIterationLimit);
        }
    }, [isOpen, effectiveToolIterationLimit]);

    const persistAccessSettings = useCallback(async (partialSettings) => {
        if (scopedWorkspaceId) {
            await workspaceManager.updateWorkspaceAccessSettings(scopedWorkspaceId, partialSettings);
            return;
        }

        if (!currentConversationId || !currentConversation) {
            return;
        }

        const conversationPayload = {};
        if (
            Object.prototype.hasOwnProperty.call(partialSettings, 'toolIterationLimit')
            || Object.prototype.hasOwnProperty.call(partialSettings, 'activeToolIds')
        ) {
            const nextToolSettings = {
                ...(currentConversation.toolSettings || {}),
            };

            if (Object.prototype.hasOwnProperty.call(partialSettings, 'toolIterationLimit')) {
                nextToolSettings.toolIterationLimit = partialSettings.toolIterationLimit;
            }

            if (Object.prototype.hasOwnProperty.call(partialSettings, 'activeToolIds')) {
                nextToolSettings.activeToolIds = partialSettings.activeToolIds;
            }

            conversationPayload.toolSettings = nextToolSettings;
        }
        if (Object.prototype.hasOwnProperty.call(partialSettings, 'activeServerIds')) {
            conversationPayload.mcpSettings = {
                ...(currentConversation.mcpSettings || {}),
                activeServerIds: partialSettings.activeServerIds,
            };
        }

        if (Object.keys(conversationPayload).length > 0) {
            await updateConversation(currentConversationId, conversationPayload);
        }
    }, [currentConversation, currentConversationId, scopedWorkspaceId, updateConversation]);

    /* ========================
     * State Synchronization Effect - Ensures user state matches their permissions
     * Deactivates tools if user access changes (e.g., beta status revoked, tier change)
     * ========================*/
    useEffect(() => {
        if (!isOpen || areSettingsLoading || effectiveActiveToolIds.length === 0) {
            return;
        }

        const activeIds = new Set(effectiveActiveToolIds);
        const disabledToolIds = new Set(availableTools.filter(t => t.isDisabled).map(t => t.id));

        const idsToDeactivate = new Set([...activeIds].filter(id => disabledToolIds.has(id)));

        if (idsToDeactivate.size > 0) {
            console.log('[Shelby] Deactivating tools due to permission changes:', [...idsToDeactivate]);
            const newActiveToolIdsArray = [...activeIds].filter(id => !idsToDeactivate.has(id));
            persistAccessSettings({ activeToolIds: newActiveToolIdsArray }).catch(error => {
                console.error("[Shelby] Failed to auto-deactivate disabled tools:", error);
            });
        }
    }, [isOpen, areSettingsLoading, availableTools, effectiveActiveToolIds, persistAccessSettings]);

    const currentBotForModal = useMemo(() => {
        return activeBotConfig ? {
            id: activeBotConfig.botId,
            name: activeBotConfig.uiName,
            description: activeBotConfig.description,
            isCustomBot: activeBotConfig.isCustomBot,
            isVanillaBot: activeBotConfig.isVanillaMode,
        } : null;
    }, [activeBotConfig]);

    const currentModelForModal = useMemo(() => {
        const activeModelId = activeBotConfig?.modelId || currentConversation?.modelId;
        if (!activeModelId) return null;
        return allPlatformModels.find(model => model.id === activeModelId) || null;
    }, [activeBotConfig?.modelId, currentConversation?.modelId, allPlatformModels]);

    const currentModelHasApiKey = useMemo(() => {
        return usableModelIds.has(currentModelForModal?.id);
    }, [usableModelIds, currentModelForModal?.id]);

    const currentModelThinkingPrefs = useMemo(() => {
        if (!currentBotForModal?.id || !currentModelForModal?.id) return null;
        return getThinkingPreferences(currentBotForModal.id, currentModelForModal.id);
    }, [currentBotForModal?.id, currentModelForModal?.id, getThinkingPreferences]);

    const currentModelThinkingBudget = useMemo(() => {
        return currentModelThinkingPrefs?.budget ?? 1800;
    }, [currentModelThinkingPrefs?.budget]);
    
    const handleBotUpdatedOrCreated = useCallback(() => {}, []);
    const handleEditCurrentCustomBot = useCallback(() => {
        if (currentBotForModal?.isCustomBot && currentBotForModal?.id) {
            setShowCustomBotModal(true);
        }
    }, [currentBotForModal]);

    /* ========================
     * handleThinkingBudgetChange - Handle thinking budget changes for individual models
     * Updates token budget and enabled status via AuthManager
     * ========================*/
    const handleThinkingBudgetChange = useCallback((modelId, budget) => {
        if (currentBotForModal?.id) {
            // A budget of 0 means "disabled"
            const isEnabled = budget > 0;
            setThinkingPreference(currentBotForModal.id, modelId, isEnabled, budget);
        }
    }, [currentBotForModal?.id, setThinkingPreference]);

    const handleModelSelectionForNextMessage = useCallback(async (selectedModel) => {
        if (!currentBotForModal || !selectedModel || !currentConversationId || !updateConversation) return;
        onClose();
        botManager.setCurrentBotAndModel(currentBotForModal.id, selectedModel.id);
        const botDetails = botManager.getBotById(currentBotForModal.id);
        const modelDetails = botManager.getState().allPlatformModels.find(m => m.id === selectedModel.id);
        if (!botDetails || !modelDetails) return;
        try {
            if (updateConversationBotAndModel) {
                await updateConversationBotAndModel(currentConversationId, botDetails, modelDetails);
            } else {
                await updateConversation(currentConversationId, {
                    modelId: modelDetails.id, modelName: modelDetails.name || modelDetails.model,
                    botPlatform: modelDetails.platform, botModel: modelDetails.model,
                    botCleanModelName: modelDetails.cleanModelName || modelDetails.model,
                    modelMaxTokens: modelDetails.maxTokens || null,
                    temperature: modelDetails.temperatureRange ? (modelDetails.temperatureRange[0] + modelDetails.temperatureRange[1]) / 2 : 0.7,
                });
            }
        } catch (error) {
            
        }
    }, [currentBotForModal, currentConversationId, updateConversation, updateConversationBotAndModel, onClose]);

    useEffect(() => {
        const portalRoot = document.getElementById('portal-root') || document.body;
        setPortalContainer(portalRoot);
    }, []);

     const handleToolToggle = useCallback(async (toolIdToToggle) => {
        if (!currentConversationId || !currentConversation) return;
        const tool = availableTools.find(t => t.id === toolIdToToggle);
        if (tool?.isDisabled) return;

        const effectiveCurrentIds = new Set(effectiveActiveToolIds);

        const isCurrentlyActive = effectiveCurrentIds.has(toolIdToToggle);
        const targetState = !isCurrentlyActive;
        setOptimisticToolStates(prev => ({ ...prev, [toolIdToToggle]: targetState }));

        const newActiveToolIdsSet = new Set(effectiveCurrentIds);
        if (targetState) newActiveToolIdsSet.add(toolIdToToggle);
        else newActiveToolIdsSet.delete(toolIdToToggle);
        const newActiveToolIdsArray = Array.from(newActiveToolIdsSet);

        try {
            await persistAccessSettings({ activeToolIds: newActiveToolIdsArray });
        } catch (error) {
            console.error("Failed to update tool preferences:", error);
        } finally {
            setOptimisticToolStates(prev => { const updated = { ...prev }; delete updated[toolIdToToggle]; return updated; });
        }
    }, [availableTools, currentConversation, currentConversationId, effectiveActiveToolIds, persistAccessSettings]);

    const handleToolIterationLimitChange = useCallback((e) => {
        const newValue = parseInt(e.target.value, 10);
        setToolIterationLimit(newValue);
    }, []);
    
    const handleToolIterationLimitCommit = useCallback(async () => {
        const clampedToolIterationLimit = Math.min(
            MAX_TOOL_ITERATION_LIMIT,
            Math.max(MIN_TOOL_ITERATION_LIMIT, toolIterationLimit),
        );
        setToolIterationLimit(clampedToolIterationLimit);

        try {
            await persistAccessSettings({ toolIterationLimit: clampedToolIterationLimit });
        } catch (error) {
            // fallback handled
        }
    }, [toolIterationLimit, persistAccessSettings]);

    const handleMcpServerToggle = useCallback(async (serverIdToToggle) => {
        if (!currentConversationId || !currentConversation) return;
        const server = allUserMcpServers.find(s => s.id === serverIdToToggle);
        if (!server) return;

        const effectiveCurrentIds = new Set(effectiveActiveServerIds);

        const isCurrentlyActive = effectiveCurrentIds.has(serverIdToToggle);
        const targetState = !isCurrentlyActive;
        setOptimisticMcpStates(prev => ({ ...prev, [serverIdToToggle]: targetState }));

        const newActiveIdsSet = new Set(effectiveCurrentIds);
        if (targetState) newActiveIdsSet.add(serverIdToToggle);
        else newActiveIdsSet.delete(serverIdToToggle);
        const newActiveMcpServerIdsArray = Array.from(newActiveIdsSet);
        
        try {
            await persistAccessSettings({ activeServerIds: newActiveMcpServerIdsArray });
        } catch (error) {
            console.error("Failed to update MCP server preferences:", error);
        } finally {
            setOptimisticMcpStates(prev => { const updated = { ...prev }; delete updated[serverIdToToggle]; return updated; });
        }
    }, [allUserMcpServers, currentConversation, currentConversationId, effectiveActiveServerIds, persistAccessSettings]);

    const handleMcpServerDeleted = useCallback(async (deletedServerId) => {
        const currentActiveIds = new Set(effectiveActiveServerIds);
        if (currentActiveIds.has(deletedServerId)) {
            currentActiveIds.delete(deletedServerId);
            try {
                await persistAccessSettings({ activeServerIds: Array.from(currentActiveIds) });
            } catch (error) {
                
            }
        }
    }, [effectiveActiveServerIds, persistAccessSettings]);

    const handleMcpServerCreated = useCallback(async (newServer) => {
        setShowMcpAddEditOverlay(false);
        if (!currentConversationId || !currentConversation || !newServer?.id) return;
        const isServerGloballyEnabled = newServer.enabled === undefined ? true : Boolean(newServer.enabled);
        if (isServerGloballyEnabled) { 
            let currentActiveIds = new Set(effectiveActiveServerIds);
            currentActiveIds.add(newServer.id);
            try {
                await persistAccessSettings({ activeServerIds: Array.from(currentActiveIds) });
            } catch (error) {
                
            }
        }
    }, [currentConversationId, currentConversation, effectiveActiveServerIds, persistAccessSettings]);

    const handleMcpServerUpdated = useCallback(async (updatedServer) => {
        setShowMcpAddEditOverlay(false);
        setServerToEdit(null);
    }, []);

    const handleDeleteMcpServer = useCallback(async (serverId) => {
        setIsProcessingMcpServer(true);
        const shouldCloseOnDelete = allUserMcpServers.length === 1;
        try {
            await deleteMcpServer(serverId);
            await handleMcpServerDeleted(serverId);
            if (shouldCloseOnDelete) {
                setShowMcpManageOverlay(false);
            }
        } catch (error) {
            
        } finally {
            setIsProcessingMcpServer(false);
        }
    }, [deleteMcpServer, handleMcpServerDeleted, allUserMcpServers]);

    const handleEditMcpServer = useCallback((server) => {
        setServerToEdit(server);
        setShowMcpManageOverlay(false);
        setShowMcpAddEditOverlay(true);
    }, []);

    const handleCloseAddEdit = useCallback(() => {
        setShowMcpAddEditOverlay(false);
        setServerToEdit(null);
    }, []);

    const modelsForCurrentBot = useMemo(() => {
        const merged = [...allPlatformModels];
        if (currentModelForModal && !merged.some(model => model.id === currentModelForModal.id)) {
            merged.unshift(currentModelForModal);
        }
        return merged;
    }, [allPlatformModels, currentModelForModal]);

    const { starredModels, hiddenModels } = useMemo(() => {
        if (!currentBotForModal?.id) return { starredModels: [], hiddenModels: [] };
        return getModelPreferencesForBot(currentBotForModal.id);
    }, [currentBotForModal?.id, getModelPreferencesForBot]);

    const visibleModels = useMemo(() => sortModelsWithPlatformPriority(modelsForCurrentBot.filter(m => !hiddenModels.includes(m.id)), starredModels), [modelsForCurrentBot, hiddenModels, starredModels]);
    const hiddenModelsList = useMemo(() => sortModelsWithPlatformPriority(modelsForCurrentBot.filter(m => hiddenModels.includes(m.id)), starredModels), [modelsForCurrentBot, hiddenModels, starredModels]);
    const allUIAvailableBots = useMemo(() => botManager.getAvailableBotsForUI(), [botManagerState.allBots]);

     const handleToggleStar = useCallback((modelId, newStarState) => {
        if (currentBotForModal?.id) starModel(currentBotForModal.id, modelId, newStarState);
    }, [currentBotForModal?.id, starModel]);

    const handleToggleHide = useCallback((modelId, newHideState) => {
        if (currentBotForModal?.id) hideModel(currentBotForModal.id, modelId, newHideState);
    }, [currentBotForModal?.id, hideModel]);
    
    const handleSwitchBotAndModel = useCallback(async (selectedBot) => {
        setIsBotSwitcherOpen(false);
        if (!selectedBot || !currentConversationId || !updateConversationBotAndModel) return;
        const modelsForNewBot = allPlatformModels;
        if (modelsForNewBot.length === 0) return;
        const prefs = getModelPreferencesForBot(selectedBot.id);
        const sortedNewBotModels = sortModelsWithPlatformPriority(modelsForNewBot, prefs.starredModels || []);
        const modelToSelect = sortedNewBotModels.find(m => !prefs.hiddenModels?.includes(m.id)) || sortedNewBotModels[0];
        if (!modelToSelect) return;
        botManager.setCurrentBotAndModel(selectedBot.id, modelToSelect.id);
        const fullNewBotDetails = botManager.getBotById(selectedBot.id);
        const fullNewModelDetails = botManager.getState().allPlatformModels.find(m => m.id === modelToSelect.id);
        if (!fullNewBotDetails || !fullNewModelDetails) return;
        updateConversationBotAndModel(currentConversationId, fullNewBotDetails, fullNewModelDetails)
            .catch(() => {  });
    }, [onClose, currentConversationId, updateConversationBotAndModel, allPlatformModels, getModelPreferencesForBot]);

    if (!isOpen || !portalContainer) return null;

    const overlaysActive = showMcpAddEditOverlay || showMcpManageOverlay;
    
    const toolsToShow = showAllTools ? availableTools : availableTools.slice(0, 6);

    const modalContent = (
        <motion.div className="fixed inset-0 z-[5500] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={() => !overlaysActive && onClose()} />
            <motion.div className="relative bg-surface rounded-lg shadow-xl border border-[var(--border-color)] w-full max-w-[777px] max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden"
                initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.1 }}
                onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center px-4 py-2.5 border-b border-[var(--border-color)] flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-medium text-on-surface">Model & Tool Options</h2>
                        <span data-tooltip-id="next-message-options-info" data-tooltip-content="These settings apply to the current conversation. Your choices are saved as your new default."><Info size={14} className="text-on-surface-dim hover:text-primary cursor-help" /></span>
                        <Tooltip id="next-message-options-info" place="bottom" style={modalTooltipStyles} border={modalTooltipBorder} noArrow float />
                    </div>
                    <button onClick={onClose} disabled={overlaysActive} className="p-1 rounded-md hover:bg-surface-dark transition-colors disabled:opacity-50"><X size={18} className="text-on-surface-dim" /></button>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-0">
                    <div className="md:col-span-5 bg-surface-dark/20 p-3 border-r border-[var(--border-color)] flex flex-col min-h-0 relative">
                        <div className="mb-3 flex-shrink-0 flex justify-between items-start">
                            <div className="flex-1">
                                <h3 className="text-xs font-medium text-on-surface-dim mb-0.5 flex items-center justify-between">
                                    <span>Your current model:</span>
                                    <div className="flex items-center gap-1"> 
                                        {currentBotForModal?.isCustomBot && (
                                            <button onClick={handleEditCurrentCustomBot} className="p-1 rounded-md text-on-surface-dim hover:text-primary hover:bg-surface-dark transition" title="Edit Custom Bot">
                                                <GearIcon size={12} />
                                            </button>
                                        )}
                                        <button onClick={() => setIsBotSwitcherOpen(true)} className="flex items-center gap-1 px-2 py-1 text-xs text-primary bg-primary/10 hover:bg-primary/20 rounded transition-colors flex-shrink-0" title="Switch to a different custom profile">
                                            <span>Switch Custom Profile</span><ArrowRight size={12} />
                                        </button>
                                    </div>
                                </h3>
                                <div className="mt-1 rounded-lg border border-[var(--border-color)] bg-surface/80 px-3 py-2.5">
                                    <div className="flex items-start justify-between gap-3 min-w-0">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {currentModelForModal?.platform ? (
                                                <PlatformTag platform={currentModelForModal.platform} hasKey={currentModelHasApiKey} size="sm" isDarkTheme={isDarkTheme} />
                                            ) : null}
                                            <p className="text-sm font-semibold text-on-surface truncate">
                                                {currentModelForModal?.name || currentModelForModal?.cleanModelName || currentModelForModal?.model || 'No model selected'}
                                            </p>
                                        </div>
                                        {currentModelForModal?.id && currentBotForModal?.id ? (
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-[10px] uppercase tracking-wide text-on-surface-dim">Thinking</span>
                                                <ThinkingControls
                                                    thinkingBudget={currentModelThinkingBudget}
                                                    onBudgetChange={(budget) => handleThinkingBudgetChange(currentModelForModal.id, budget)}
                                                    modelId={currentModelForModal.id}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    {currentBotForModal?.name && (
                                        <p className="mt-1 text-[11px] text-on-surface-dim/80 truncate">
                                            Custom profile: {currentBotForModal.name}
                                        </p>
                                    )}
                                    {currentModelForModal?.id && currentBotForModal?.id ? (
                                        <div className="mt-3 rounded-md border border-[var(--border-color)] bg-surface-dark/30 px-2.5 py-2">
                                            <div className="flex items-center justify-between gap-3 mb-1.5">
                                                <div className="flex items-center gap-1.5 min-w-0">
                                                    <Brain size={12} className="text-primary shrink-0" />
                                                    <span className="text-xs font-medium text-on-surface">Thinking</span>
                                                    <span className="text-[10px] text-on-surface-dim truncate">
                                                        {currentModelThinkingBudget > 0 ? `${currentModelThinkingBudget.toLocaleString()} tokens` : 'Off'}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleThinkingBudgetChange(currentModelForModal.id, currentModelThinkingBudget > 0 ? 0 : 1800);
                                                    }}
                                                    className="text-[10px] px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                                                >
                                                    {currentModelThinkingBudget > 0 ? 'Disable' : 'Enable'}
                                                </button>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="32768"
                                                step="256"
                                                value={currentModelThinkingBudget}
                                                onChange={(e) => handleThinkingBudgetChange(currentModelForModal.id, parseInt(e.target.value, 10))}
                                                className="w-full h-1.5 rounded-lg appearance-none cursor-pointer slider"
                                                style={getRangeFillStyle(currentModelThinkingBudget, 0, 32768)}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <h3 className="text-xs font-medium text-on-surface-dim mb-1.5 flex-shrink-0">Select Model</h3>
                        <div
                            className="flex-1 overflow-y-auto custom-scrollbar space-y-0.5 px-1"
                            style={{ maxHeight: 'calc(15 * 2.4rem)' }}
                        >
                            {visibleModels.map(model => (
                                <ModelVariationItem key={model.id} modelInfo={model}
                                    isSelected={activeBotConfig?.modelId === model.id}
                                    onSelect={handleModelSelectionForNextMessage}
                                    hasApiKey={usableModelIds.has(model.id)}
                                    isStarred={starredModels.includes(model.id)} isHidden={false}
                                    onToggleStar={handleToggleStar} onToggleHide={handleToggleHide} 
                                    thinkingPrefs={currentBotForModal?.id ? getThinkingPreferences(currentBotForModal.id, model.id) : null}
                                    onThinkingBudgetChange={handleThinkingBudgetChange}
                                    isDarkTheme={isDarkTheme}
                                     />
                            ))}
                            {hiddenModelsList.length > 0 && (
                                <>
                                    <div className="border-t border-[var(--border-color)] mt-1.5 pt-1.5">
                                        <button onClick={() => setShowHiddenModelsUIToggle(!showHiddenModelsUIToggle)} className="flex items-center gap-1.5 w-full px-1 py-0.5 text-xs text-on-surface-dim hover:text-on-surface transition-colors rounded hover:bg-surface-dark">
                                            <ChevronDown size={12} className={`transition-transform ${showHiddenModelsUIToggle ? 'rotate-180' : ''}`} /> Hidden Models ({hiddenModelsList.length})
                                        </button>
                                    </div>
                                    {showHiddenModelsUIToggle && (<div className="space-y-0.5 mt-1">
                                        {hiddenModelsList.map(model => (<ModelVariationItem key={model.id} modelInfo={model} isSelected={false} onSelect={handleModelSelectionForNextMessage} hasApiKey={usableModelIds.has(model.id)} isStarred={starredModels.includes(model.id)} isHidden={true} onToggleStar={handleToggleStar} onToggleHide={handleToggleHide} thinkingPrefs={currentBotForModal?.id ? getThinkingPreferences(currentBotForModal.id, model.id) : null} onThinkingBudgetChange={handleThinkingBudgetChange} isDarkTheme={isDarkTheme} />))}
                                    </div>)}
                                </>
                            )}
                            {visibleModels.length === 0 && hiddenModelsList.length === 0 && (<p className="text-xs text-on-surface-dim/70 py-2 text-center">No models available for this profile.</p>)}
                        </div>
                        <AnimatePresence>
                            {isBotSwitcherOpen && (<BotSwitcherOverlay isOpen={isBotSwitcherOpen} onClose={() => setIsBotSwitcherOpen(false)} onSelectBot={handleSwitchBotAndModel} currentBotId={activeBotConfig?.botId} availableBots={allUIAvailableBots} />)}
                        </AnimatePresence>
                    </div>

                    <div className="md:col-span-7 p-3 flex flex-col min-h-0 relative"> 
                        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1">
                            
                            <div className="space-y-0.5">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-medium text-on-surface flex items-center"><ToolIcon size={16} className="mr-1.5 text-primary" /> Built-in Tools</h4>
                                </div>
                                {areSettingsLoading ? (<p className="text-xs text-on-surface-dim/70 px-1 py-1">Loading tools...</p>)
                                : availableTools.length > 0 ? (
                                    <div className="divide-y divide-border-color">
                                        {toolsToShow.map((tool) => {
                                            let isToolEffectivelyActive;
                                            if (tool.isDisabled) {
                                                isToolEffectivelyActive = false;
                                            } else {
                                                if (optimisticToolStates.hasOwnProperty(tool.id)) {
                                                    isToolEffectivelyActive = optimisticToolStates[tool.id];
                                                } else if (effectiveActiveToolIds.includes(tool.id)) {
                                                    isToolEffectivelyActive = true;
                                                } else {
                                                        isToolEffectivelyActive = false;
                                                }
                                            }
                                            return (<div key={tool.id} >
                                                <PanelToggleSwitch 
                                                    id={`tool-toggle-${tool.id}`} 
                                                    label={<div className="flex items-center gap-1"><span>{tool.name}</span>{tool.isMaintenance && (<span className="text-[8px] px-1 py-[1px] rounded-sm font-medium uppercase tracking-wide" style={{ background: 'rgba(var(--primary-rgb), 0.15)', color: 'rgba(var(--primary-rgb), 0.7)' }}>{tool.maintenanceLabel}</span>)}{tool.requiredTierTag && (<span className="text-[9px] px-1 py-0.5 bg-primary/20 text-primary rounded-sm font-medium">{tool.requiredTierTag}</span>)}</div>}
                                                    subLabel={tool.isDisabled && tool.disabledReason 
                                                        ? <span className="text-amber-500/80 font-medium">{tool.disabledReason}</span> 
                                                        : tool.description
                                                    } 
                                                    enabled={isToolEffectivelyActive} 
                                                    onChange={() => handleToolToggle(tool.id)} 
                                                    disabled={areSettingsLoading || tool.isDisabled || overlaysActive} />
                                            </div>);
                                        })}
                                        {availableTools.length > 6 && (
                                            <div>
                                                <button
                                                    onClick={() => setShowAllTools(!showAllTools)}
                                                    className="flex items-center gap-1.5 w-full px-1.5 py-2 text-xs text-on-surface-dim hover:text-on-surface transition-colors rounded"
                                                >
                                                    <ChevronDown size={12} className={`transition-transform ${showAllTools ? 'rotate-180' : ''}`} />
                                                    {showAllTools ? 'Show less' : `Show ${availableTools.length - 6} more tools`}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (<p className="text-xs text-on-surface-dim/70 px-1 py-1">No tools available for your account.</p>)}
                                
                                <div className="mt-4 px-2">
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="text-xs text-on-surface-dim font-medium">Agent Autonomy Limit (Actions)</label>
                                        <span className="text-xs text-on-surface font-semibold">{toolIterationLimit}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={MIN_TOOL_ITERATION_LIMIT}
                                        max={MAX_TOOL_ITERATION_LIMIT}
                                        value={toolIterationLimit}
                                        onChange={handleToolIterationLimitChange}
                                        onMouseUp={handleToolIterationLimitCommit}
                                        onTouchEnd={handleToolIterationLimitCommit}
                                        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer slider"
                                        style={getRangeFillStyle(toolIterationLimit, MIN_TOOL_ITERATION_LIMIT, MAX_TOOL_ITERATION_LIMIT)}
                                    />
                                    <p className="text-[10px] text-on-surface-dim/70 mt-1">Default is 6. Max consecutive actions an agent can take per turn.</p>
                                </div>
                            </div>
                            
                            <div className="pt-4 mt-2 border-t-2 border-[var(--border-color)]">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-medium text-on-surface flex items-center"><Server size={16} className="mr-1.5 text-primary" /> MCP Servers</h4>
                                    {hasTierAccess(TIERS.PRO) && (
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => setShowMcpAddEditOverlay(true)}
                                                className="flex items-center gap-1 bg-primary/20 hover:bg-primary/30 text-primary text-xs font-medium py-1 px-2 rounded-md transition-colors disabled:opacity-50"
                                                title="Add New Server"
                                                disabled={overlaysActive}
                                            >
                                                <PlusCircle size={12} /> Add New
                                            </button>
                                            <button
                                                onClick={() => setShowMcpManageOverlay(true)}
                                                className="p-1 rounded-md hover:bg-surface-dark/50 text-on-surface-dim hover:text-primary transition-colors disabled:opacity-50"
                                                title="Manage Servers"
                                                disabled={overlaysActive}
                                            >
                                                <GearIcon size={14} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {hasTierAccess(TIERS.PRO) ? (
                                    allUserMcpServers.length > 0 ? (
                                        <div className="divide-y divide-border-color">
                                            {allUserMcpServers.map((server) => {
                                                let isServerActiveInConversation;
                                                if (optimisticMcpStates.hasOwnProperty(server.id)) {
                                                    isServerActiveInConversation = optimisticMcpStates[server.id];
                                                } else if (effectiveActiveServerIds.includes(server.id)) {
                                                    isServerActiveInConversation = true;
                                                } else {
                                                    isServerActiveInConversation = false;
                                                }
                                                return (
                                                    <div key={server.id}>
                                                        <PanelToggleSwitch
                                                            id={`mcp-server-toggle-${server.id}`}
                                                            label={ <span className="flex items-center gap-1.5"> {server.name} {!server.enabled && (<span className="text-[9px] text-amber-500/80" title="Global default is OFF">(Default Off)</span>)} </span> }
                                                            subLabel={server.description || `Tool ID: ${server.callableToolId || server.id}`}
                                                            enabled={isServerActiveInConversation}
                                                            onChange={() => handleMcpServerToggle(server.id)}
                                                            disabled={overlaysActive}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-on-surface-dim/70 px-1 py-1">No MCP servers configured. Click "Add New" to get started.</p>
                                    )
                                 ) : (
                                    <div className="mt-2 text-center p-3 rounded-md bg-surface-dark/40 border border-dashed border-border-color">
                                        <h5 className="text-xs font-semibold text-on-surface">MCP Servers are a Pro Feature</h5>
                                        <p className="text-[11px] text-on-surface-dim/80 mt-1">
                                            Upgrade to a Pro plan to connect to your own custom tool servers for unlimited extensibility.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <AnimatePresence>
                            {showMcpAddEditOverlay && (
                                <McpServerAddEditOverlay
                                    isOpen={showMcpAddEditOverlay}
                                    onClose={handleCloseAddEdit}
                                    onServerCreated={handleMcpServerCreated}
                                    onServerUpdated={handleMcpServerUpdated}
                                    existingServers={allUserMcpServers}
                                    addMcpServer={addMcpServer}
                                    updateMcpServer={updateMcpServer}
                                    serverToEdit={serverToEdit}
                                />
                            )}
                            {showMcpManageOverlay && (
                                <McpServerManageOverlay
                                    isOpen={showMcpManageOverlay}
                                    onClose={() => setShowMcpManageOverlay(false)}
                                    servers={allUserMcpServers}
                                    onEditServer={handleEditMcpServer}
                                    onDeleteServer={handleDeleteMcpServer}
                                    isProcessing={isProcessingMcpServer}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>
            <CustomBotModal isOpen={showCustomBotModal} onClose={() => setShowCustomBotModal(false)} botId={currentBotForModal?.id} onBotUpdated={handleBotUpdatedOrCreated} />
        </motion.div>
    );
    return ReactDOM.createPortal(modalContent, portalContainer);
};

export default NextMessageOptionsModal;